import { useState } from 'react';

interface ProductImageGalleryProps {
  images: string[];
  productName: string;
  badge?: string | null;
  discount?: number | null;
}

export default function ProductImageGallery({ images, productName, badge, discount }: ProductImageGalleryProps) {
  const [selected, setSelected] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });

  const validImages = images.filter(Boolean);
  if (validImages.length === 0) {
    validImages.push('https://public.readdy.ai/ai/img_res/58104b9617afadd46496d8b3dfa3d1ea.jpg');
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMousePos({ x, y });
  };

  const prev = () => setSelected(i => (i - 1 + validImages.length) % validImages.length);
  const next = () => setSelected(i => (i + 1) % validImages.length);

  return (
    <div className="flex gap-6">
      {/* Sol: Thumbnail listesi (dikey) */}
      {validImages.length > 1 && (
        <div className="flex flex-col gap-2 flex-shrink-0">
          {validImages.map((img, i) => (
            <button
              key={i}
              onClick={() => setSelected(i)}
              className={`relative flex-shrink-0 rounded-xl overflow-hidden border-2 transition-all duration-200 cursor-pointer bg-cream ${
                selected === i
                  ? 'border-gold'
                  : 'border-cream-dark hover:border-coffee-light'
              }`}
              style={{ width: '64px', aspectRatio: '2/3' }}
            >
              <img
                src={img}
                alt={`${productName} görsel ${i + 1}`}
                className="w-full h-full object-cover object-center"
              />
            </button>
          ))}
        </div>
      )}

      {/* Sağ: Ana resim */}
      <div className="flex-1 min-w-0">
        <div
          className="relative rounded-2xl overflow-hidden bg-cream cursor-zoom-in group"
          style={{ width: '100%', maxWidth: '420px', aspectRatio: '2/3' }}
          onMouseEnter={() => setZoomed(true)}
          onMouseLeave={() => setZoomed(false)}
          onMouseMove={handleMouseMove}
        >
          <img
            src={validImages[selected]}
            alt={productName}
            className="w-full h-full object-cover object-center transition-transform duration-300"
            style={zoomed ? {
              transformOrigin: `${mousePos.x}% ${mousePos.y}%`,
              transform: 'scale(1.7)',
            } : {}}
          />

          {/* Badges */}
          <div className="absolute top-4 left-4 flex flex-col gap-2 pointer-events-none">
            {discount && discount > 0 && (
              <span className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-lg">
                %{discount} İndirim
              </span>
            )}
            {badge && !discount && (
              <span className="px-3 py-1 bg-gold text-white text-xs font-bold rounded-lg">
                {badge}
              </span>
            )}
          </div>

          {/* Zoom hint */}
          <div className={`absolute bottom-4 right-4 px-2.5 py-1.5 bg-white/80 backdrop-blur-sm rounded-lg flex items-center gap-1.5 text-xs text-coffee-light transition-opacity duration-200 pointer-events-none ${zoomed ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'}`}>
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-zoom-in-line text-sm" />
            </div>
            Yakınlaştır
          </div>

          {/* Nav arrows */}
          {validImages.length > 1 && (
            <>
              <button
                onClick={e => { e.stopPropagation(); prev(); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white cursor-pointer"
              >
                <i className="ri-arrow-up-s-line text-coffee text-lg" />
              </button>
              <button
                onClick={e => { e.stopPropagation(); next(); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white cursor-pointer"
              >
                <i className="ri-arrow-down-s-line text-coffee text-lg" />
              </button>
            </>
          )}

          {/* Dot indicators */}
          {validImages.length > 1 && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex flex-col gap-1.5">
              {validImages.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setSelected(i)}
                  className={`rounded-full transition-all duration-200 cursor-pointer ${selected === i ? 'h-5 w-1.5 bg-gold' : 'h-1.5 w-1.5 bg-cream-dark'}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
